import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { SellerAccountDetailItem } from '@shared/api/needAccount/needAccount.interface';
import {
  sellerAccountDetailExportUrl,
  sellerMonthlyAccountedUrl,
  sellerNeedAccountUrl,
} from '@shared/api/needAccount/sellerAccount.hook';
import { rebateShopOrderList } from '@shared/api/shopOrder/shopOrder.controller';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import { useId } from 'react-id-generator';
import { useQueryClient } from 'react-query';

import {
  buildBrandFilterParams,
  sellerAccountUserIdAtom,
  sellerBrandOptionsAtom,
  sellerSelectedBrandIdsAtom,
} from './accountContent.atom';

//* Admin이 해당 월의 미정산 건 전체를 일괄 상조정산 처리하기 위한 버튼+모달

interface Props {
  year: number;
  month: number;
  children?: React.ReactNode;
  onSuccess?: () => void;
}

export const RebateButton: FC<Props> = function RebateButton({
  year,
  month,
  children,
  onSuccess,
}: Props) {
  const [id] = useId(1, 'rebateButton');
  const openId = `sellerRebateButton-${id}-${year}-${month}`;

  const { sellerRole, isAdmin } = useAuth();
  const role = sellerRole();
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  // 선택된 bugoBrand → API 파라미터 변환
  const selectedBrandIds = useRecoilValue(sellerSelectedBrandIdsAtom);
  const brandOptions = useRecoilValue(sellerBrandOptionsAtom);
  const userId = useRecoilValue(sellerAccountUserIdAtom);
  const brandFilter = useMemo(
    () => buildBrandFilterParams(selectedBrandIds, brandOptions),
    [selectedBrandIds, brandOptions],
  );

  // ─── 상태 ───
  const [detailItems, setDetailItems] = useState<SellerAccountDetailItem[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());

  // ─── 데이터 fetch ───
  const fetchDetail = useCallback(async () => {
    setFetchLoading(true);
    try {
      const url = sellerAccountDetailExportUrl(
        role,
        year,
        month,
        'accounted',
        brandFilter,
        userId,
      );
      const { data } = await api_.get(url);
      const items: SellerAccountDetailItem[] = Array.isArray(data)
        ? data
        : data.data ?? [];
      setDetailItems(items);
    } catch (err) {
      console.error('상조정산 데이터 불러오기 실패', err);
    } finally {
      setFetchLoading(false);
    }
  }, [role, year, month, brandFilter, userId]);

  // ─── 계산 ───
  const totalCount = detailItems.length;
  const rebatedCount = useMemo(
    () => detailItems.filter((i) => i.isRebated).length,
    [detailItems],
  );
  const pendingCount = totalCount - rebatedCount;
  const pendingIds = useMemo(
    () => detailItems.filter((i) => !i.isRebated).map((i) => i._id),
    [detailItems],
  );
  const allIds = useMemo(() => detailItems.map((i) => i._id), [detailItems]);

  // ─── 모달 열기 ───
  const handleOpen = useCallback(() => {
    setDetailItems([]);
    setProcessing(false);
    setOpen(true);
    fetchDetail();
  }, [fetchDetail, setOpen]);

  const refetchAccountQueries = useCallback(() => {
    queryClient.invalidateQueries(sellerMonthlyAccountedUrl(sellerRole(), year, userId));
    queryClient.invalidateQueries(sellerNeedAccountUrl(sellerRole(), userId));
  }, [queryClient, sellerRole, userId, year]);

  // ─── 미정산 건만 정산 처리 ───
  const handleRebate = useCallback(async () => {
    if (pendingIds.length === 0) return;
    setProcessing(true);
    try {
      await rebateShopOrderList(pendingIds, selectedDate.toDate());
      notificateSuccess(notifiacationInstance, `${pendingIds.length}건 상조정산 완료`);
      refetchAccountQueries();
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      console.error('상조정산 처리 실패', err);
      notificateError(notifiacationInstance, '상조정산 처리 실패');
    } finally {
      setProcessing(false);
    }
  }, [
    pendingIds,
    selectedDate,
    notifiacationInstance,
    setOpen,
    onSuccess,
    refetchAccountQueries,
  ]);

  // ─── 전체 건 정산일 변경 (이미 완료된 건 포함) ───
  const handleRebateAll = useCallback(async () => {
    if (allIds.length === 0) return;
    setProcessing(true);
    try {
      await rebateShopOrderList(allIds, selectedDate.toDate());
      notificateSuccess(
        notifiacationInstance,
        `전체 ${allIds.length}건 상조정산일 변경 완료`,
      );
      refetchAccountQueries();
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      console.error('상조정산일 변경 실패', err);
      notificateError(notifiacationInstance, '상조정산일 변경 실패');
    } finally {
      setProcessing(false);
    }
  }, [
    allIds,
    selectedDate,
    notifiacationInstance,
    setOpen,
    onSuccess,
    refetchAccountQueries,
  ]);

  return (
    <>
      {/* 트리거 */}
      {children ? (
        <div onClick={handleOpen} className="cursor-pointer">
          {children}
        </div>
      ) : (
        <Button
          onClick={handleOpen}
          disabled={!isAdmin()}
          className="shadow-sm theme-text-4 theme-bg-main"
        >
          상조 정산
        </Button>
      )}

      {/* 모달 (하나로 통합: 로딩 → 내용) */}
      <Modal openId={openId}>
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-4 py-2">
          {fetchLoading ? (
            /* ── 로딩 중 ── */
            <div className="flex flex-col items-center gap-3">
              <Spin size="large" />
              <p className="text-sm text-gray-500">데이터를 불러오는 중...</p>
            </div>
          ) : (
            <>
              <p className="text-base font-bold">
                {year}년 {month}월 상조정산
              </p>

              {/* 건수 현황 */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500">
                  전체 <span className="font-medium text-gray-800">{totalCount}</span>건
                </span>
                <span className="text-green-600">
                  완료 <span className="font-medium">{rebatedCount}</span>건
                </span>
                {pendingCount > 0 && (
                  <span className="text-orange-500">
                    미정산 <span className="font-bold">{pendingCount}</span>건
                  </span>
                )}
              </div>

              {/* 날짜 선택 */}
              <div className="pt-2">
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  allowClear={false}
                />
              </div>

              {/* 버튼 영역 */}
              <div className="flex items-center gap-2 pt-1">
                {pendingCount > 0 && (
                  <button
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={processing}
                    onClick={handleRebate}
                  >
                    {processing ? '처리중...' : `미정산 ${pendingCount}건 처리`}
                  </button>
                )}
                <button
                  className="rounded-md bg-gray-500 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={processing || totalCount === 0}
                  onClick={handleRebateAll}
                >
                  {processing ? '처리중...' : `전체 ${totalCount}건 날짜변경`}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};
